import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ArcadeSpotBookingDialog } from './ArcadeSpotBookingDialog';
import { AuthDialog } from './AuthDialog';
import { Gamepad2, Calendar, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { generateSEOAltText } from '@/utils/seoAltTextGenerator';
import { OptimizedImage } from '@/components/OptimizedImage';

interface ArcadeSpot {
  id: string;
  position: number;
  image_url: string;
  website_url?: string;
  server_id: string;
  start_date: string;
  end_date: string;
  status: string;
  servers?: {
    name: string;
  };
}

export const ArcadeSpots = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [activeSpots, setActiveSpots] = useState<ArcadeSpot[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const authDialogTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    fetchActiveSpots();
  }, []);

  const fetchActiveSpots = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('arcade_spots')
        .select(`
          *,
          servers (
            name
          )
        `)
        .in('status', ['active', 'suspended', 'paid'])
        .gte('end_date', today);

      if (error) throw error;
      
      // Pas de filtrage ici - on montre tous les spots mais on cache juste le badge Admin
      const filteredData = data || [];
      
      setActiveSpots(filteredData);
    } catch (error) {
      console.error('Error fetching arcade spots:', error);
    }
  };

  const getSpotByPosition = (position: number) => {
    return activeSpots.find(spot => spot.position === position);
  };

  const handleSpotClick = (position: number) => {
    //console.log('ArcadeSpots: Spot clicked, position:', position);
    const spot = getSpotByPosition(position);
    //console.log('ArcadeSpots: Found spot:', spot);
    
    const today = new Date().toISOString().split('T')[0];
    const spotStartsToday = spot?.start_date === today;
    
    if (spot && spot.website_url && (spot.status === 'active' || (spot.status === 'paid' && spotStartsToday))) {
      //console.log('ArcadeSpots: Opening website:', spot.website_url);
      window.open(spot.website_url, '_blank');
    } else if (!spot) {
      // Slot libre - vérifier si l'utilisateur est connecté
      if (!user) {
        authDialogTriggerRef.current?.click();
        return;
      }
      // Ouvrir le dialogue de réservation
      //console.log('ArcadeSpots: Opening booking dialog for position:', position);
      setSelectedPosition(position);
      setIsDialogOpen(true);
    }
    // Si suspendu, ne rien faire (pas de lien cliquable)
  };

  const positions = [
    { number: 1, title: t('arcade.leftSpot') },
    { number: 2, title: t('arcade.centerSpot') },
    { number: 3, title: t('arcade.rightSpot') }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
      {positions.map((pos) => {
        const spot = getSpotByPosition(pos.number);
        const isOccupied = !!spot;
        const isSuspended = spot?.status === 'suspended';
        const isPaid = spot?.status === 'paid';
        const isActive = spot?.status === 'active';
        const today = new Date().toISOString().split('T')[0];
        const spotStartsToday = spot?.start_date === today;
        const spotStartsFuture = spot && spot.start_date > today;
        
        return (
          <div
            key={pos.number}
            onClick={() => handleSpotClick(pos.number)}
            className={`
              relative group w-full max-w-2xl mx-auto aspect-[16/9] rounded-lg border-2 transition-all duration-300 overflow-hidden
              ${isOccupied 
                ? isSuspended
                  ? 'border-slate-600/50 bg-slate-800 cursor-not-allowed'
                  : isPaid
                    ? 'border-gaming-accent/50 hover:border-gaming-accent bg-slate-800 shadow-lg shadow-gaming-accent/20 cursor-pointer'
                    : 'border-gaming-primary/50 hover:border-gaming-primary bg-slate-800 shadow-lg shadow-gaming-primary/20 animate-arcade-pulse cursor-pointer'
                : 'border-slate-700/50 hover:border-gaming-secondary/70 bg-slate-800 hover:bg-slate-700 cursor-pointer'
              }
            `}
          >
            {isOccupied && spot ? (
              <>
                {/* Image de la publicité */}
                <OptimizedImage
                  src={spot.image_url}
                  alt={generateSEOAltText({
                    type: 'arcade-spot',
                    name: `${pos.number}`,
                    context: spot.server_id ? 'serveur' : undefined,
                    language
                  })}
                  imageType="banner"
                  width={640}
                  height={360}
                  loading="eager"
                  priority={true}
                  className={`w-full h-full object-cover ${isSuspended ? 'grayscale blur-sm opacity-50' : isActive ? 'animate-arcade-image-pulse' : ''}`}
                  fallback={
                    <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                      <Gamepad2 className="w-10 h-10 text-slate-500" />
                    </div>
                  }
                />
                
                {/* Overlay avec effet au survol (seulement si actif ou démarre aujourd'hui) */}
                {!isSuspended && (isActive || (isPaid && spotStartsToday)) && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500 ease-out flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out transform scale-95 group-hover:scale-100 flex items-center gap-2 text-white font-semibold">
                    <ExternalLink className="w-4 h-4" />
                    <span className="text-sm">{t('arcade.visit')}</span>
                  </div>
                  </div>
                )}

                {/* Overlay pour les spots réservés (payés mais pas encore actifs) */}
                {isPaid && spotStartsFuture && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="bg-gaming-accent/90 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {t('arcade.reserved')} • {t('arcade.startsOn')} {new Date(spot.start_date).toLocaleDateString()}
                    </div>
                  </div>
                )}

                {/* Overlay pour les spots suspendus */}
                {isSuspended && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="bg-red-500/80 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {t('arcade.suspended')}
                    </div>
                  </div>
                )}

                {/* Badge du serveur - cacher si c'est Arcade Spot Admin */}
                {spot.servers?.name && spot.servers.name !== 'Arcade Spot Admin' && (
                  <div className={`absolute bottom-2 left-2 backdrop-blur-sm text-white text-xs px-2 py-1 rounded ${isSuspended ? 'bg-red-700/70' : 'bg-black/70'}`}>
                    {spot.servers.name}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Slot libre */}
                <div className="flex flex-col items-center justify-center h-full text-center p-4">
                  <div className="w-12 h-12 bg-gaming-secondary/20 rounded-full flex items-center justify-center mb-3 group-hover:bg-gaming-secondary/30 transition-colors">
                    <Gamepad2 className="w-6 h-6 text-gaming-secondary" />
                  </div>
                  <h3 className="font-bold text-white text-sm mb-1">{pos.title}</h3>
                  <p className="text-slate-400 text-xs">{t('arcade.clickToReserve')}</p>
                </div>

                {/* Effet lumineux au survol */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-r from-gaming-secondary/10 via-gaming-primary/10 to-gaming-accent/10 rounded-lg"></div>
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gaming-secondary via-gaming-primary to-gaming-accent"></div>
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-gaming-accent via-gaming-primary to-gaming-secondary"></div>
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* Bouton de réservation unique */}
      <div className="col-span-full mt-6 text-center">
        <button
          onClick={() => {
            // Vérifier si l'utilisateur est connecté
            if (!user) {
              authDialogTriggerRef.current?.click();
              return;
            }
            //console.log('ArcadeSpots: Book button clicked');
            setSelectedPosition(null); // Pas de position prédéfinie
            setIsDialogOpen(true);
            //console.log('ArcadeSpots: Dialog should be open, isDialogOpen:', true);
          }}
          className="px-6 py-3 bg-slate-800 hover:bg-slate-700 border border-gaming-primary/50 hover:border-gaming-primary rounded-lg text-gaming-primary font-semibold transition-all duration-300 hover:scale-105"
        >
          <div className="flex items-center gap-2 justify-center">
            <Calendar className="w-4 h-4" />
            {t('arcade.bookSpot')}
          </div>
        </button>
        <p className="text-slate-500 text-xs mt-2">
          {t('arcade.bookingDescription')}
        </p>
      </div>

      {/* Dialog de réservation */}
      <ArcadeSpotBookingDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedPosition(null);
        }}
        position={selectedPosition}
        onSuccess={() => {
          setIsDialogOpen(false);
          setSelectedPosition(null);
          fetchActiveSpots();
        }}
      />

      {/* Dialog d'authentification (caché, déclenché par ref) */}
      <AuthDialog defaultTab="signin">
        <button ref={authDialogTriggerRef} className="hidden" />
      </AuthDialog>
    </div>
  );
};